import React, { useState } from 'react';
import { Modal } from '@attentive-platform/stem-ui';

import { ExampleCardsStyles as S } from './styles';

const isImageType = type => String(type || '').toLowerCase() === 'image';

/** Normalize API `urls` to `{ url, type }` (also accepts legacy string urls). */
export const normalizeExampleUrls = urls =>
  (urls || [])
    .map(entry => {
      if (!entry) return null;
      if (typeof entry === 'string') {
        return { url: entry, type: 'link' };
      }
      const url = entry.url || entry.href;
      if (!url) return null;
      return { url, type: String(entry.type || 'link').toLowerCase() };
    })
    .filter(Boolean);

const ImageCard = ({ title, imageUrl, onOpen }) => (
  <S.Card type='button' onClick={onOpen} title={title}>
    <S.Thumb>
      <S.ThumbImg src={imageUrl} alt={title} />
    </S.Thumb>
    <S.CardBody>
      <S.CardTitle>{title}</S.CardTitle>
      <S.CardCaption>Click to zoom</S.CardCaption>
    </S.CardBody>
  </S.Card>
);

const OpenInNewIcon = () => (
  <S.ChipIcon aria-hidden viewBox='0 0 24 24' width='14' height='14'>
    <path d='M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42L17.59 5H14V3ZM19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2v7Z' />
  </S.ChipIcon>
);

const LinkChip = ({ label, onOpen }) => (
  <S.LinkChip type='button' onClick={onOpen} title={`${label} (opens in new tab)`}>
    <OpenInNewIcon />
    <span>{label}</span>
  </S.LinkChip>
);

/**
 * Example modifiers: each `urls[]` entry is `{ url, type }`.
 * - type `image` → card with thumbnail; click opens zoom modal
 * - type `link` (or other) → compact chip; click opens url in new tab
 */
const ExampleCards = ({ modifiers, showHeading = true }) => {
  const [preview, setPreview] = useState(null);

  const items = (modifiers || []).flatMap(m => {
    const entries = normalizeExampleUrls(m.urls);
    const baseTitle = m.title || 'Sample takeoff';

    if (!entries.length) return [];

    return entries.map((entry, i) => {
      const isImage = isImageType(entry.type);
      return {
        key: `${m.id}-${i}`,
        title: baseTitle,
        type: entry.type,
        url: entry.url,
        isImage
      };
    });
  });

  if (!items.length) return null;

  const handleOpen = item => {
    if (!item.url) return;
    if (item.isImage) {
      setPreview(item);
      return;
    }
    window.open(item.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <S.Section>
        {showHeading && <S.Heading>Examples</S.Heading>}
        <S.Grid>
          {items.map(item =>
            item.isImage ? (
              <ImageCard
                key={item.key}
                title={item.title}
                imageUrl={item.url}
                onOpen={() => handleOpen(item)}
              />
            ) : (
              <LinkChip key={item.key} label='Sample takeoff' onOpen={() => handleOpen(item)} />
            )
          )}
        </S.Grid>
      </S.Section>

      <Modal
        show={!!preview}
        onClose={() => setPreview(null)}
        heading={<span className='pl-4'>{preview?.title || 'Example'}</span>}
        content={
          <S.ModalBody>
            {preview?.url ? <S.ModalImg src={preview.url} alt={preview.title} /> : null}
          </S.ModalBody>
        }
      />
    </>
  );
};

export default ExampleCards;
